// A collection of TurfJS related utility functions
import bbox from '@turf/bbox'
import bboxPolygon from '@turf/bbox-polygon'
import booleanIntersects from '@turf/boolean-intersects'
import buffer from '@turf/buffer'
import flatten from '@turf/flatten'
import { featureCollection } from '@turf/helpers'

import { getDistanceBetweenPixels } from './MapUtils'

export const bufferAsync = async (...args) => {
  const buffer = (await import('@turf/buffer')).default

  return buffer(...args)
}

/*
Much of the code base is built around the assumption that the current target contains a single feature, for instance, to inject geometry into
dataset queries, provide styling, do geometry queries such as centroid, highlighting current target, and more.

Additionally, adding support for MultiPoint, MultiLine and MultiPolygon as a target would add significantly to the complexity of the target system.

We use the combine function to achieve a single feature representation. Unfortunately, when combine encounters multiple feature types it creates
a FeatureCollection with up to three features; MultiPoint, MultiLine and MultiPolygon. https://turfjs.org/docs/#combine

In order to achieve compatibility between single click selection (Point features) and the region style selections (Polygon features)
we need to find a common format for the final combined Feature. Clearly this is MultiPolygon.

So, to achieve this outcome without re-writing the entire targeting system, we convert point features to Polygon features with the smallest possible area
which allows combine to create a single multipolygon feature.

Note we only do this when absolutely necessary, that is, when there is a control key multiple selection.

*/
export const convertFeatureCollectionFeaturesToPolygons = (featureCollectionT) => {
  // console.debug('convertFeatureCollectionFeaturesToPolygons', featureCollectionT)

  const convertedFC = featureCollection([])

  if (!featureCollectionT?.features || featureCollectionT.features.length === 0) {
    console.log('convertFeatureCollectionFeaturesToPolygons - no features to convert', featureCollectionT)

    return convertedFC
  }

  // Flatten the feature collection into multiple features in case it is of type GeometryCollection
  const flattenedFC = flatten(featureCollectionT)

  // const types = [...new Set(flattenedFC.features.map(feature => feature.geometry?.type))]
  // console.debug('convertFeatureCollectionFeaturesToPolygons - feature types', types)

  for (const index in flattenedFC.features) {
    const feature = flattenedFC.features[index]
    const type = feature?.geometry?.type
    if (type === 'Point' || type === 'MultiPoint') {
      const polygonFeature = buffer(feature, 0.01, { units: 'meters' }) // Smallest it would go without breaking.ESRI's ArcGIS (actually: 0.002 is the smallest)
      const polygonFeatureBbox = bboxPolygon(bbox(polygonFeature))
      convertedFC.features.push(polygonFeatureBbox)
    } else if (type === 'LineString' || type === 'MultiLineString') {
      const featureBuffered = buffer(feature, 0.01, { units: 'meters' }) // Smallest it would go without breaking.ESRI's ArcGIS (actually: 0.002 is the smallest)
      convertedFC.features.push(featureBuffered)
    } else {
      convertedFC.features.push(feature)
    }
  }

  // console.debug('convertFeatureCollectionFeaturesToPolygons - feature collection', convertedFC)

  return convertedFC
}

export const featureCollectionIsPointsOnly = (fc) => {
  // console.debug('featureCollectionIsPointsOnly', fc)

  const types = [...new Set(fc.features.map(feature => feature.geometry?.type))]
  // console.debug('featureCollectionIsPointsOnly - feature types', types)

  const isPointsOnly = types.every(type => type === 'Point' || type === 'MultiPoint')

  // console.debug('featureCollectionIsPointsOnly - feature collection is points only', isPointsOnly, types)

  return isPointsOnly
}

export const featureCollectionIsSinglePoint = (fc) => {
  const isSinglePoint = fc?.features?.length === 1 && fc.features[0].geometry?.type === 'Point'

  // console.debug('featureCollectionIsSinglePoint', isSinglePoint, fc)

  return isSinglePoint
}

// The following function returns all features from a GeoJSON data object that intersect
// with another GeoJSON data object. It can handle any kind of geometry.
export const getIntersectionOfFeatureCollections = (geojson1, geojson2) => {
  const intersectionFeatures = []

  // Filter out features without geometry
  const geojson1Features = (geojson1.features ?? []).filter(feature => !!feature.geometry)
  const geojson2Features = (geojson2.features ?? []).filter(feature => !!feature.geometry)

  // Loop through the features in geojson1
  for (let mainFeature of geojson1Features) {
    // Loop through the features in geojson2

    // If the feature is a polygon, we need to apply a small negative buffer to prevent adjacent
    // polygons being caught up in the intersection
    const geomType = mainFeature.geometry?.type || ''
    const geomTypeIsPolygon = !!geomType.match(/polygon/i)

    // Apply a negative buffer of 10 centimeters to avoid adjacent polygons being caught up in
    // the intersection, or add a small buffer (of 3 pixel) to the feature to allow for better
    // line (and point) intersections
    const bufferDistance = geomTypeIsPolygon ? -0.1 : getDistanceBetweenPixels([0, 0], [0, 3])

    // console.debug('getIntersectionOfFeatureCollections - bufferDistance', bufferDistance)

    // Apply a buffer to the main feature
    const bufferedFeature = buffer(mainFeature, bufferDistance, { units: 'meters' })
    if (bufferedFeature) {
      // console.debug('getIntersectionOfFeatureCollections - point/line bufferedgeom', mainFeature, bufferedFeature)
      mainFeature = bufferedFeature
    }

    // console.debug('getIntersectionOfFeatureCollections - geomType', geomType)
    for (const feature2 of geojson2Features) {
      // Check if the features intersect
      // console.debug('getIntersectionOfFeatureCollections - booleanIntersects', feature1, feature2)
      try {
        // Sometimes TurfJS throws an error during booleanIntersects
        const featuresIntersect = booleanIntersects(mainFeature, feature2)
        // const featuresIntersect = booleanWithin(feature2, feature1) || booleanOverlap(feature1, feature2)
        if (featuresIntersect) {
          // Add the intersecting feature to the result array
          intersectionFeatures.push(feature2)
        }
      } catch (error) {
        console.warn('getIntersectionOfFeatureCollections - booleanIntersects error!', error, mainFeature, feature2)
      }
    }
  }

  return {
    type: 'FeatureCollection',
    features: intersectionFeatures
  }
}
